import { HttpClient } from '@adapters/HttpClient';
import { MinimumFee, MinimumFeesFilters, MinimumFeesPage, SelectOption } from '../models/MinimumFee';

interface MinimumFeesApiMeta {
  page?: number;
  currentPage?: number;
  current_page?: number;
  per_page?: number;
  perPage?: number;
  total?: number;
  last_page?: number;
  lastPage?: number;
}

interface MinimumFeesApiResponse {
  data: MinimumFee[];
  meta?: MinimumFeesApiMeta;
}

interface SearchResponse<T> {
  data?: T[];
  meta?: MinimumFeesApiMeta;
}

const toPositiveInt = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const normalizeMeta = (
  meta: MinimumFeesApiMeta | undefined,
  fallbackPage: number,
  fallbackPerPage: number,
  itemCount: number,
) => {
  const page = toPositiveInt(meta?.page ?? meta?.currentPage ?? meta?.current_page, fallbackPage);
  const perPage = toPositiveInt(meta?.per_page ?? meta?.perPage, fallbackPerPage);
  const total = toPositiveInt(meta?.total, itemCount);
  const lastPage =
    toPositiveInt(meta?.last_page ?? meta?.lastPage, 0) || Math.max(1, Math.ceil(total / Math.max(1, perPage)));

  return {
    page,
    per_page: perPage,
    total,
    last_page: lastPage,
  };
};

const normalizeOptionId = (value: unknown): string => (value == null ? '' : String(value));

export class MinimumFeesApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getMinimumFees(filters: MinimumFeesFilters = {}): Promise<MinimumFeesPage> {
    const { team_id, year, month, status, invoice_id, page = 1, per_page = 25 } = filters;

    const params: Record<string, string | number> = {
      page,
      per_page,
      ...(team_id ? { team_id } : {}),
      ...(year ? { year } : {}),
      ...(month ? { month } : {}),
      ...(status ? { status } : {}),
      ...(invoice_id ? { invoice_id } : {}),
    };

    const response = await this.httpClient.get<MinimumFeesApiResponse>(
      '/accounting/minimum-fees',
      params,
      {},
      page === 1,
    );

    const data = (response.data ?? []).map((fee) => ({
      ...fee,
      status: String(fee.status ?? '').toLowerCase() as MinimumFee['status'],
    }));

    return {
      data,
      meta: normalizeMeta(response.meta, page, per_page, data.length),
    };
  }

  async invoiceMinimumFees(ids: string[]): Promise<unknown> {
    return this.httpClient.post('/accounting/minimum-fees/invoice', { ids });
  }

  async searchTeams(params: { search?: string; page?: number; limit?: number }): Promise<{
    options: SelectOption[];
    hasMore: boolean;
  }> {
    const page = params.page ?? 1;
    const limit = params.limit ?? 50;
    const response = await this.httpClient.get<SearchResponse<{ id: string | number; name: string }>>(
      '/teams-child',
      {
        page,
        per_page: limit,
        ...(params.search?.trim() ? { name: params.search.trim() } : {}),
      },
      {},
      false,
    );

    const teams = response.data ?? [];
    const meta = normalizeMeta(response.meta, page, limit, teams.length);

    return {
      options: teams.map((team) => ({
        label: team.name,
        value: normalizeOptionId(team.id),
      })),
      hasMore: meta.page < meta.last_page,
    };
  }

  async getTeamOption(id: string): Promise<SelectOption | null> {
    if (!id) return null;

    try {
      const team = await this.httpClient.get<{ id: string | number; name: string }>(
        `/teams-child/${id}`,
        {},
        {},
        false,
      );
      return {
        label: team.name,
        value: normalizeOptionId(team.id),
      };
    } catch {
      return null;
    }
  }

  async searchInvoices(params: { search?: string; page?: number; limit?: number }): Promise<{
    options: SelectOption[];
    hasMore: boolean;
  }> {
    const page = params.page ?? 1;
    const limit = params.limit ?? 50;
    const response = await this.httpClient.get<
      SearchResponse<{
        id: string | number;
        erp_number?: string | null;
        invoice_number?: string | null;
        preinvoice?: { code?: string | null } | null;
      }>
    >(
      '/invoices',
      {
        page,
        per_page: limit,
        ...(params.search?.trim() ? { search: params.search.trim() } : {}),
      },
      {},
      false,
    );

    const invoices = response.data ?? [];
    const meta = normalizeMeta(response.meta, page, limit, invoices.length);

    return {
      options: invoices.map((invoice) => ({
        label:
          invoice.erp_number ??
          invoice.invoice_number ??
          invoice.preinvoice?.code ??
          `#${normalizeOptionId(invoice.id)}`,
        value: normalizeOptionId(invoice.id),
      })),
      hasMore: meta.page < meta.last_page,
    };
  }

  async getInvoiceOption(id: string): Promise<SelectOption | null> {
    if (!id) return null;

    try {
      const invoice = await this.httpClient.get<{
        id: string | number;
        erp_number?: string | null;
        invoice_number?: string | null;
        preinvoice?: { code?: string | null } | null;
      }>(`/invoices/${id}`, {}, {}, false);

      return {
        label:
          invoice.erp_number ??
          invoice.invoice_number ??
          invoice.preinvoice?.code ??
          `#${normalizeOptionId(invoice.id)}`,
        value: normalizeOptionId(invoice.id),
      };
    } catch {
      return null;
    }
  }
}
